import type { AriaAttributes, PropsWithChildren, ReactNode } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useId } from '../hooks/useId'
import { NestedListActionTypes } from './Actions'
import Item, { useItem } from './Item'
import type { ListNestedLevel } from './ListContext'
import { ListContext } from './ListContext'

export interface ListProps {
  /**
   * Accessible label for the list.
   * Only use if further context is needed and/or subheader is not present
   */
  'aria-label'?: AriaAttributes['aria-label']
  /**
   * List items to be rendered within the list
   */
  children?: ReactNode | ReactNode[]
  /**
   * If true, reduces vertical padding for a more compact appearance
   * @default false
   */
  dense?: boolean
  /**
   * The nesting level of the list: None=0, One=1, Two=2, Three=3, Four=4. Used for visual indentation
   * @default 0
   */
  nested?: ListNestedLevel
  /**
   * Optional header text displayed above the list.
   * If the header is not present, you may provide an `aria-label` for accessibility
   */
  subheader?: ReactNode
}

function List({
  // Label on list item not usually necessary. Only use if further context
  // is needed (and/or subheader is not present)
  // https://www.scottohara.me/blog/2020/05/02/labelled-lists.html
  'aria-label': label,
  children,
  dense = false,
  nested = 0,
  subheader,
}: PropsWithChildren<ListProps>): JSX.Element | null {
  const subheaderId = `coconut-list-subheader-${useId()}`
  const itemDispatch = useItem()?.[1]

  const [state, setState] = useState({
    dense,
    nested,
    id: `coconut-list-${useId()}`,
  })

  useEffect(() => {
    setState((prev) => ({ ...prev, dense, nested }))
  }, [dense, nested])

  useEffect(() => {
    // Notifies controlling item of ID so we can link through aria-controls.
    itemDispatch?.({
      type: NestedListActionTypes.RegisterListId,
      id: state.id,
    })

    return () =>
      itemDispatch?.({ type: NestedListActionTypes.UnregisterListId })
  }, [itemDispatch, state.id])

  // We don't provide an aria-labelledby alternative option as it would
  // reference the subheader text, which is already rendered on the page
  // and obvious/accessible to everyone.
  const accessibleProps = useMemo(() => {
    const labels = label
      ? {
          'aria-label': label,
        }
      : {}

    return labels
  }, [label])

  return (
    <ListContext.Provider value={state}>
      {nested ? (
        <ul
          className="flex overflow-hidden flex-col"
          {...accessibleProps}
          id={state.id}
        >
          {children}
        </ul>
      ) : (
        <div className="flex flex-col h-full py-2">
          {subheader ? (
            <div
              className="px-4 font-sans text-sm font-medium tracking-wide leading-12 text-black-60"
              id={subheaderId}
            >
              {subheader}
            </div>
          ) : null}
          <ul {...accessibleProps}>{children}</ul>
        </div>
      )}
    </ListContext.Provider>
  )
}

List.Item = Item

export default List
